import { userManagementApi } from './userManagementService';

// Tipuri pentru erori sistem
export interface SystemError {
  id: string;
  type: 'database' | 'api' | 'auth' | 'payment' | 'email' | 'sms' | 'file' | 'network' | 'validation' | 'business' | 'system';
  category: 'error' | 'warning' | 'info' | 'debug';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  stack?: string;
  context?: {
    userId?: string;
    clinicId?: string;
    sessionId?: string;
    requestId?: string;
    userAgent?: string;
    ip?: string;
    endpoint?: string;
    method?: string;
    [key: string]: any;
  };
  metadata?: {
    component?: string;
    action?: string;
    version?: string;
    environment?: string;
    [key: string]: any;
  };
  tags?: string[];
  isResolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  resolution?: string;
  assignedTo?: string;
  createdAt: string;
  updatedAt: string;
  occurrences: number;
  lastOccurrence: string;
  relatedErrors?: string[];
}

// Tipuri pentru filtre erori
export interface ErrorFilters {
  type?: string;
  category?: string;
  severity?: string;
  isResolved?: boolean;
  assignedTo?: string;
  startDate?: string;
  endDate?: string;
  clinicId?: string;
  userId?: string;
  tags?: string[];
  search?: string;
  page?: number;
  limit?: number;
}

// Tipuri pentru statistici erori
export interface ErrorStats {
  total: number;
  unresolved: number;
  byType: {
    [key: string]: number;
  };
  bySeverity: {
    low: number;
    medium: number;
    high: number;
    critical: number;
  };
  byCategory: {
    error: number;
    warning: number;
    info: number;
    debug: number;
  };
  trends: {
    date: string;
    count: number;
    severity: string;
  }[];
  topErrors: {
    id: string;
    title: string;
    occurrences: number;
    lastOccurrence: string;
  }[];
  resolutionTime: {
    average: number;
    median: number;
    p95: number;
  };
}

// Tipuri pentru reguli de alerta
export interface ErrorAlertRule {
  id: string;
  name: string;
  description: string;
  conditions: {
    type?: string;
    severity?: string;
    category?: string;
    threshold: number;
    timeWindow: number; // in minute
  };
  actions: {
    type: 'email' | 'sms' | 'webhook' | 'slack' | 'teams';
    config: any;
  }[];
  isActive: boolean;
  clinicId?: string;
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru grupuri de erori
export interface ErrorGroup {
  id: string;
  name: string;
  description: string;
  errors: string[];
  isResolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  resolution?: string;
  createdAt: string;
  updatedAt: string;
}

class ErrorsService {
  private baseUrl = '/master-admin/errors';

  // Erori
  async getErrors(filters?: ErrorFilters): Promise<{ errors: SystemError[]; total: number; page: number; limit: number; }> {
    try {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      if (filters?.category) params.append('category', filters.category);
      if (filters?.severity) params.append('severity', filters.severity);
      if (filters?.isResolved !== undefined) params.append('isResolved', filters.isResolved.toString());
      if (filters?.assignedTo) params.append('assignedTo', filters.assignedTo);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.clinicId) params.append('clinicId', filters.clinicId);
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.tags) params.append('tags', filters.tags.join(','));
      if (filters?.search) params.append('search', filters.search);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await userManagementApi.get(`${this.baseUrl}?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getErrorById(id: string): Promise<SystemError> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async resolveError(id: string, resolution: string, assignedTo?: string): Promise<SystemError> {
    try {
      const response = await userManagementApi.patch(`${this.baseUrl}/${id}/resolve`, {
        resolution,
        assignedTo
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async unresolveError(id: string): Promise<SystemError> {
    try {
      const response = await userManagementApi.patch(`${this.baseUrl}/${id}/unresolve`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async assignError(id: string, assignedTo: string): Promise<SystemError> {
    try {
      const response = await userManagementApi.patch(`${this.baseUrl}/${id}/assign`, {
        assignedTo
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async addErrorTag(id: string, tag: string): Promise<SystemError> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/${id}/tags`, { tag });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async removeErrorTag(id: string, tag: string): Promise<SystemError> {
    try {
      const response = await userManagementApi.delete(`${this.baseUrl}/${id}/tags/${tag}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteError(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      throw error;
    }
  }

  async bulkResolveError(ids: string[], resolution: string, assignedTo?: string): Promise<void> {
    try {
      await userManagementApi.patch(`${this.baseUrl}/bulk/resolve`, {
        ids,
        resolution,
        assignedTo
      });
    } catch (error) {
      throw error;
    }
  }

  async bulkDeleteError(ids: string[]): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/bulk/delete`, {
        data: { ids }
      });
    } catch (error) {
      throw error;
    }
  }

  // Statistici erori
  async getErrorStats(filters?: { startDate?: string; endDate?: string; clinicId?: string; }): Promise<ErrorStats> {
    try {
      const params = new URLSearchParams();
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.clinicId) params.append('clinicId', filters.clinicId);

      const response = await userManagementApi.get(`${this.baseUrl}/stats?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Reguli de alerta
  async getErrorAlertRules(clinicId?: string): Promise<{ rules: ErrorAlertRule[]; total: number; }> {
    try {
      const params = new URLSearchParams();
      if (clinicId) params.append('clinicId', clinicId);

      const response = await userManagementApi.get(`${this.baseUrl}/alert-rules?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getErrorAlertRuleById(id: string): Promise<ErrorAlertRule> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/alert-rules/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createErrorAlertRule(rule: Omit<ErrorAlertRule, 'id' | 'createdAt' | 'updatedAt'>): Promise<ErrorAlertRule> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/alert-rules`, rule);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateErrorAlertRule(id: string, rule: Partial<ErrorAlertRule>): Promise<ErrorAlertRule> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/alert-rules/${id}`, rule);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteErrorAlertRule(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/alert-rules/${id}`);
    } catch (error) {
      throw error;
    }
  }

  async testErrorAlertRule(id: string): Promise<{ success: boolean; message: string; }> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/alert-rules/${id}/test`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Grupuri de erori
  async getErrorGroups(filters?: { isResolved?: boolean; clinicId?: string; }): Promise<{ groups: ErrorGroup[]; total: number; }> {
    try {
      const params = new URLSearchParams();
      if (filters?.isResolved !== undefined) params.append('isResolved', filters.isResolved.toString());
      if (filters?.clinicId) params.append('clinicId', filters.clinicId);

      const response = await userManagementApi.get(`${this.baseUrl}/groups?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getErrorGroupById(id: string): Promise<ErrorGroup> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/groups/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createErrorGroup(group: Omit<ErrorGroup, 'id' | 'createdAt' | 'updatedAt'>): Promise<ErrorGroup> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/groups`, group);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateErrorGroup(id: string, group: Partial<ErrorGroup>): Promise<ErrorGroup> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/groups/${id}`, group);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteErrorGroup(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/groups/${id}`);
    } catch (error) {
      throw error;
    }
  }

  async addErrorsToGroup(groupId: string, errorIds: string[]): Promise<ErrorGroup> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/groups/${groupId}/errors`, {
        errorIds
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async removeErrorsFromGroup(groupId: string, errorIds: string[]): Promise<ErrorGroup> {
    try {
      const response = await userManagementApi.delete(`${this.baseUrl}/groups/${groupId}/errors`, {
        data: { errorIds }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Export erori
  async exportErrors(filters?: ErrorFilters): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      if (filters?.category) params.append('category', filters.category);
      if (filters?.severity) params.append('severity', filters.severity);
      if (filters?.isResolved !== undefined) params.append('isResolved', filters.isResolved.toString());
      if (filters?.assignedTo) params.append('assignedTo', filters.assignedTo);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.clinicId) params.append('clinicId', filters.clinicId);
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.tags) params.append('tags', filters.tags.join(','));
      if (filters?.search) params.append('search', filters.search);

      const response = await userManagementApi.get(`${this.baseUrl}/export?${params.toString()}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Testare erori
  async createTestError(error: Partial<SystemError>): Promise<SystemError> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/test`, error);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Monitorizare in timp real
  async subscribeToErrors(_callback: (error: SystemError) => void): Promise<() => void> {
    // Implementare WebSocket sau Server-Sent Events
    // Pentru moment, returnam o functie mock
    return () => {};
  }
}

export const errorsService = new ErrorsService();
